import React, { useState, useEffect } from 'react';

import { v4 as uuid } from 'uuid';

import { Space, Button, Select, Input, Form, Modal, InputNumber, Tooltip, Row, Col, Dropdown, Flex, Popconfirm, Spin } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';
import { DeleteFilled, LoadingOutlined, PlusOutlined, RollbackOutlined } from '@ant-design/icons';

import TradingPlatform from '@controls/trading-platform/trading-platform';

import { exception, error } from '@extensions/notification';

import { useAppDispatch } from '@store/hooks';
import { userLoaded } from '@store/actions';

import IBaseComponentProps from '@components/base-component-props';

import { IMarking } from '@entities/marking';
import { ICountryItem } from '@entities/country-item';
import { ICountry } from '@entities/country';
import { IPreloadingHeader } from '@entities/preloading-header';
import { IPreloadingLine } from '@entities/preloading-line';
import { IConsignee } from '@entities/consignee';
import { ITradingPlatform } from '@entities/trading-platform';
import { ICargoCarrier } from '@entities/cargo-carrier';

import { PreloadingStatus } from '@enums/preloading-status';

import './preloading-items.css';

const dayjs = require('dayjs');
var utc = require('dayjs/plugin/utc');
dayjs.extend(utc);

interface ILineMarking {
    lineId: string;
    consigneeId?: string;
    code?: string;
}

interface IPreloadingItemsProps extends IBaseComponentProps {
    headerId?: string;
    headerCountry?: ICountry;
    baseCountries: Array<ICountry>;
    api: NotificationInstance;
    onSave: () => void;
    onCancel: () => void;
}

const PreloadingItems = (props: IPreloadingItemsProps) => {
    const { headerId, headerCountry, baseCountries, api, onSave, onCancel, serverFetch } = props;

    const [form] = Form.useForm();
    const [newMarkingForm] = Form.useForm();

    const d = useAppDispatch();

    const [header, setHeader] = useState<IPreloadingHeader>();

    const [markings, setMarkings] = useState<Array<IMarking>>([]);
    const [countryItems, setCountryItems] = useState<Array<ICountryItem>>([]);
    const [consignees, setConsignees] = useState<Array<IConsignee>>([]);
    const [lines, setLines] = useState<Array<IPreloadingLine>>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [currentCountry, setCurrentCountry] = useState<ICountry>();
    const [cargoCarriers, setCargoCarriers] = useState<Array<ICargoCarrier>>([]);

    const [refreshMarkingsRequired, setRefreshMarkingsRequired] = useState<boolean>(true);
    const [assignMarkingOpen, setAssignMarkingOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        setLoading(true);

        const fetchData = async () => {
            const promises = [
                await serverFetch(`countries/items`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`consignees`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`tradingplatforms`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cargocarriers/air', { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения авиаперевозчиков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            if (headerId) {
                promises.push(
                    await serverFetch(`preloading/${headerId}`, { method: 'GET' })
                        .then((data: any) => {
                            return data;
                        })
                        .catch((ex: any) => {
                            exception(api, 'Ошибка получения поставки', ex, () => d(userLoaded(undefined)));
                        }),
                );
                promises.push(
                    await serverFetch(`preloading/lines`, { method: 'GET', queryParams: { headerId: headerId } })
                        .then((data: any) => {
                            return data;
                        })
                        .catch((ex: any) => {
                            exception(api, 'Ошибка получения поставки', ex, () => d(userLoaded(undefined)));
                        }),
                );
            }

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountryItems(result[0][0]);
                setConsignees(result[0][1]);

                let serverPlatforms: Array<ITradingPlatform> = result[0][2];

                let copyCountries = [...baseCountries];
                copyCountries.map((c: ICountry) => {
                    let tmpPlatforms = serverPlatforms.filter((sp) => sp.countryId == c.id);
                    c.platforms = [...tmpPlatforms];
                });

                setCountries(copyCountries);

                setCargoCarriers(result[0][3]);

                let countryId = headerCountry?.id;

                if (headerId) {
                    var loadedHeader: IPreloadingHeader = result[0][4];
                    if (loadedHeader) {
                        countryId = loadedHeader.countryId;
                        setHeader(loadedHeader);

                        form.setFieldValue('id', loadedHeader.id);
                        form.setFieldValue('countryId', loadedHeader.countryId);
                        form.setFieldValue('awbNumber', loadedHeader.awbNumber);
                        form.setFieldValue('cargoCarrierId', loadedHeader.cargoCarrierId);
                    }

                    var loadedLines = result[0][5];
                    if (loadedLines && loadedLines.length > 0) {
                        setLines(JSON.parse(JSON.stringify(loadedLines)));
                    } else {
                        var newLines: Array<IPreloadingLine> = [
                            { id: uuid(), status: PreloadingStatus.Processed, newMarkingRequired: false },
                        ];
                        setLines(newLines);
                    }
                } else {
                    setHeader({
                        id: undefined,
                        countryId: headerCountry?.id,
                        countryName: headerCountry?.name,
                        countryCode: headerCountry?.code,
                    });

                    form.setFieldValue('countryId', headerCountry?.id);

                    var newLines: Array<IPreloadingLine> = [{ id: uuid(), status: PreloadingStatus.Processed, newMarkingRequired: false }];
                    setLines(newLines);
                }

                const country = baseCountries?.find((d) => d.id === countryId);
                setCurrentCountry(country);

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            setLines([]);
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        if (!refreshMarkingsRequired) return;

        setRefreshMarkingsRequired(false);

        serverFetch('markings', { method: 'GET' })
            .then((data: any) => {
                setMarkings(data);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения маркировок', ex, () => d(userLoaded(undefined)));
            });
    }, [refreshMarkingsRequired]);

    const loadPlatforms = () => {
        serverFetch(`tradingplatforms`, { method: 'GET' })
            .then((data: any) => {
                let tmpCountries = [...baseCountries];

                tmpCountries.map((c: ICountry) => {
                    let tmpPlatforms = data.filter((d: ITradingPlatform) => d.countryId == c.id);
                    c.platforms = [...tmpPlatforms];
                });

                setCountries(tmpCountries);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения торговых площадок', ex, () => d(userLoaded(undefined)));
            });
    };

    const onAddNewMarking = (entity: ILineMarking) => {
        serverFetch(`markings`, { method: 'POST', bodyData: entity })
            .then((data: string) => {
                setRefreshMarkingsRequired(true);

                setTimeout(() => {
                    let copyLines = [...lines];
                    let line = copyLines?.find((d) => d.id === entity.lineId);
                    if (line) {
                        line.markingId = data;
                        form.setFieldValue(`${entity.lineId}markingId`, data);
                    }

                    setLines(copyLines);
                    setAssignMarkingOpen(false);

                    newMarkingForm.resetFields();
                }, 1000);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения маркировки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSaveHeader = (data: IPreloadingHeader) => {
        if (header && header.entryId) {
            const totalQty: number = lines.reduce((sum, current) => sum + +(current.qty || 0), 0);
            const totalGrossWeight: number = lines.reduce((sum, current) => sum + +(current.grossWeight || 0), 0);
            const totalVolumeWeight: number = lines.reduce((sum, current) => sum + +(current.volumeWeight || 0), 0);

            if (header.qty != totalQty) {
                error(api, 'Количество не совпадает');
                return;
            }

            if (header.grossWeight != totalGrossWeight) {
                error(api, 'Вес брутто не совпадает');
                return;
            }

            if (header.volumeWeight != totalVolumeWeight) {
                error(api, 'Объемный вес не совпадает');
                return;
            }

            const noMarking = lines.find((l) => !l.markingId);
            if (noMarking) {
                error(api, `Маркировка "${noMarking.markingCode}" не найдена в системе`);
                return;
            }
        }

        data.lines = lines;

        serverFetch(`preloading`, { method: data.id ? 'PUT' : 'POST', bodyData: data })
            .then(() => {
                onSave();
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка сохранения поставки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onMenuClick = (e: any, id: string | undefined) => {
        switch (e.key) {
            case 'edit': {
                let copyLines = [...lines];
                let line = copyLines?.find((d) => d.id === id);
                if (line) {
                    line.newMarkingRequired = true;
                }

                setLines(copyLines);

                break;
            }
            case 'delete': {
                let copyLines = [...lines];
                copyLines = copyLines.filter((g) => g.id != id);

                setLines(copyLines);
            }
        }
    };

    const actionItems = [
        {
            key: 'edit',
            label: 'Изменить',
        },
        {
            key: 'delete',
            label: 'Удалить',
        },
    ];

    return (
        <>
            <Modal
                destroyOnHidden={true}
                width={1015}
                title={!header ? '' : header.id ? 'Изменить партию' : 'Добавить партию'}
                open={true}
                okText='ОК'
                onOk={() => {
                    form.submit();
                }}
                onCancel={() => onCancel()}
            >
                {!loading ? (
                    <Form colon={false} style={{ marginTop: 20 }} form={form} onFinish={onSaveHeader}>
                        <Form.Item name='id' initialValue={header?.id} hidden>
                            <Input />
                        </Form.Item>

                        {header?.entryId && (
                            <Form.Item hidden initialValue={header?.countryId} name='countryId'>
                                <Input />
                            </Form.Item>
                        )}

                        <Row gutter={24}>
                            <Col span={6}>
                                {header?.entryId ? (
                                    <Form.Item initialValue={header.countryName} label='Страна' name='countryName'>
                                        <Input disabled={true} />
                                    </Form.Item>
                                ) : (
                                    <Form.Item
                                        initialValue={header?.countryId}
                                        label='Страна'
                                        name='countryId'
                                        rules={[{ required: true, message: 'Укажите страну' }]}
                                    >
                                        <Select
                                            autoFocus
                                            onChange={(value) => {
                                                let country = countries?.find((d) => d.id === value);
                                                setCurrentCountry(country);
                                            }}
                                            showSearch={{
                                                filterOption: (input, option) =>
                                                    (option?.label as string).toLowerCase().includes(input.toLowerCase()),

                                                filterSort: (a, b) =>
                                                    (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                            }}
                                            options={countries.map((c) => {
                                                return { value: c.id, label: c.name };
                                            })}
                                        />
                                    </Form.Item>
                                )}
                            </Col>
                            <Col span={currentCountry?.code == 'NL' ? 12 : 6}>
                                {header?.entryId ? (
                                    <Form.Item initialValue={header?.awbNumber} label='AWB' name='awbNumber'>
                                        <Input disabled={true} />
                                    </Form.Item>
                                ) : (
                                    <Form.Item
                                        initialValue={header?.awbNumber}
                                        name={'awbNumber'}
                                        label={currentCountry?.code === 'NL' ? 'Поставщик' : 'AWB'}
                                        rules={[
                                            {
                                                required: true,
                                                message: currentCountry?.code === 'NL' ? 'Укажите поставщика' : 'Укажите AWB',
                                            },
                                        ]}
                                    >
                                        {currentCountry && currentCountry?.code === 'NL' ? (
                                            <TradingPlatform
                                                defaultPlatformName={undefined}
                                                country={currentCountry}
                                                basePlatforms={currentCountry.platforms || []}
                                                api={api}
                                                onSelect={(value: string) => {
                                                    form.setFieldValue('awbNumber', value);
                                                }}
                                                loadPlatforms={loadPlatforms}
                                                serverFetch={serverFetch}
                                            />
                                        ) : (
                                            <Input />
                                        )}
                                    </Form.Item>
                                )}
                            </Col>

                            {currentCountry?.code !== 'NL' && (
                                <Col span={8}>
                                    <Form.Item label='Авиаперевозчик' name='cargoCarrierId' initialValue={header?.cargoCarrierId}>
                                        <Select
                                            allowClear
                                            onChange={(value) => {
                                                form.setFieldValue('cargoCarrierId', value);
                                            }}
                                            showSearch={{
                                                filterOption: (input, option) =>
                                                    (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),

                                                filterSort: (a, b) =>
                                                    (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                                            }}
                                            options={cargoCarriers
                                                .filter((c) => c.countryId == currentCountry?.id)
                                                .map((c) => {
                                                    return { value: c.id, label: c.name };
                                                })}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>

                        {header && (
                            <Row gutter={24}>
                                <Col span={6}>
                                    <Form.Item label='Брутто'>
                                        <Space.Compact>
                                            <Form.Item noStyle initialValue={header.grossWeight} name='grossWeight'>
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    decimalSeparator=','
                                                    min={0}
                                                    stringMode
                                                    step={1}
                                                    onChange={(value: number | null) => {
                                                        setHeader({ ...header, grossWeight: value });
                                                    }}
                                                />
                                            </Form.Item>
                                            <Space.Addon>кг</Space.Addon>
                                        </Space.Compact>
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item label='Объемный'>
                                        <Space.Compact>
                                            <Form.Item noStyle initialValue={header.volumeWeight} name='volumeWeight'>
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    decimalSeparator=','
                                                    min={0}
                                                    stringMode
                                                    step={1}
                                                    onChange={(value: number | null) => {
                                                        setHeader({ ...header, volumeWeight: value });
                                                    }}
                                                />
                                            </Form.Item>
                                            <Space.Addon>кг</Space.Addon>
                                        </Space.Compact>
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}

                        <table style={{ borderSpacing: 0 }}>
                            <thead>
                                <tr className='preloading-item-header'>
                                    <th style={{ padding: 8 }} scope='col'>
                                        <Flex align='center'>
                                            {(!header || !header.entryId) && (
                                                <Tooltip title='Добавить груз'>
                                                    <Button
                                                        style={{ width: 37 }}
                                                        type='primary'
                                                        icon={<PlusOutlined />}
                                                        onClick={() => {
                                                            let copyLines = [...lines];
                                                            copyLines.push({
                                                                id: uuid(),
                                                                status: PreloadingStatus.Processed,
                                                                newMarkingRequired: false,
                                                            });

                                                            setLines(copyLines);
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}

                                            <Flex style={{ width: '100%' }} justify='center'>
                                                Маркировка
                                            </Flex>
                                        </Flex>
                                    </th>
                                    <th scope='col'>Продукт</th>
                                    <th scope='col'>Кол-во / шт</th>
                                    <th scope='col'>Брутто / кг</th>
                                    <th scope='col'>Объемный / кг</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l) => {
                                    return (
                                        <tr key={l.id}>
                                            <td className='preloading-item-cell'>
                                                {l.markingId || l.newMarkingRequired || !l.markingCode ? (
                                                    <Form.Item noStyle>
                                                        <Space.Compact>
                                                            <Popconfirm
                                                                title='Удалить выбранный груз?'
                                                                onConfirm={() => {
                                                                    let copyLines = [...lines];
                                                                    copyLines = copyLines.filter((g) => g.id != l.id);
                                                                    setLines(copyLines);
                                                                }}
                                                            >
                                                                <Button
                                                                    icon={<DeleteFilled />}
                                                                    variant='link'
                                                                    color='blue'
                                                                    disabled={lines.length <= 1}
                                                                />
                                                            </Popconfirm>
                                                            <Form.Item
                                                                noStyle
                                                                initialValue={l.markingId}
                                                                required
                                                                name={`${l.id}markingId`}
                                                                rules={[{ required: true, message: 'Укажите маркировку' }]}
                                                            >
                                                                <Select
                                                                    style={{ width: l.newMarkingRequired ? 218 : 250 }}
                                                                    autoFocus={!!header}
                                                                    onChange={(value) => {
                                                                        let line = lines?.find((d) => d.id === l.id);
                                                                        if (line) {
                                                                            const marking = markings.find((m) => m.id === value);

                                                                            line.markingId = marking?.id;
                                                                            line.markingCode = marking?.code;
                                                                        }
                                                                    }}
                                                                    showSearch={{
                                                                        filterOption: (input, option) =>
                                                                            (option?.label as string)
                                                                                .toLowerCase()
                                                                                .includes(input.toLowerCase()),
                                                                        filterSort: (a, b) =>
                                                                            (a?.label as string)
                                                                                .toLowerCase()
                                                                                .localeCompare((b?.label as string).toLowerCase()),
                                                                    }}
                                                                    options={markings.map((m) => {
                                                                        return { value: m.id, label: `${m.code} / ${m.cityName}` };
                                                                    })}
                                                                />
                                                            </Form.Item>
                                                            {l.newMarkingRequired && (
                                                                <Button
                                                                    type='primary'
                                                                    icon={<RollbackOutlined />}
                                                                    onClick={() => {
                                                                        let copyLines: Array<IPreloadingLine> = JSON.parse(
                                                                            JSON.stringify(lines),
                                                                        );
                                                                        let line = copyLines?.find((d) => d.id === l.id);
                                                                        if (line) {
                                                                            line.newMarkingRequired = false;
                                                                            line.markingId = undefined;
                                                                        }

                                                                        setLines(copyLines);
                                                                    }}
                                                                />
                                                            )}
                                                        </Space.Compact>
                                                    </Form.Item>
                                                ) : (
                                                    <Form.Item noStyle validateStatus='error' help='Маркировка не найдена'>
                                                        <Space.Compact style={{ width: '100%' }}>
                                                            <Input style={{ color: 'var(--main-red)' }} readOnly value={l.markingCode} />
                                                            <Dropdown.Button
                                                                style={{ width: 'fit-content' }}
                                                                type='primary'
                                                                menu={{ items: actionItems, onClick: (e) => onMenuClick(e, l.id) }}
                                                                onClick={() => {
                                                                    newMarkingForm.setFieldValue('lineId', l.id);
                                                                    newMarkingForm.setFieldValue('code', l.markingCode);
                                                                    setAssignMarkingOpen(true);
                                                                }}
                                                            >
                                                                <Tooltip title='Добавить в систему'>
                                                                    <PlusOutlined style={{ margin: -5 }} />
                                                                </Tooltip>
                                                            </Dropdown.Button>
                                                        </Space.Compact>
                                                    </Form.Item>
                                                )}
                                            </td>
                                            <td className='preloading-item-cell'>
                                                <Form.Item
                                                    noStyle
                                                    name={`${l.id}itemId`}
                                                    initialValue={l.itemId}
                                                    rules={[{ required: true, message: 'Укажите продукт' }]}
                                                >
                                                    <Select
                                                        style={{ width: 200, margin: '0 5px 0 10px' }}
                                                        onChange={(value) => {
                                                            let line = lines?.find((d) => d.id === l.id);
                                                            if (line) {
                                                                line.itemId = value;
                                                            }
                                                        }}
                                                        showSearch={{
                                                            filterOption: (input, option) =>
                                                                (option?.label as string).toLowerCase().startsWith(input.toLowerCase()),
                                                        }}
                                                        options={
                                                            currentCountry
                                                                ? countryItems
                                                                      .filter((i) => i.countryId == currentCountry.id)
                                                                      .map((i) => {
                                                                          return { value: i.itemId, label: i.itemName };
                                                                      })
                                                                : []
                                                        }
                                                    />
                                                </Form.Item>
                                            </td>
                                            <td className='preloading-item-cell'>
                                                <Space.Compact style={{ margin: '0 5px' }}>
                                                    <Form.Item
                                                        noStyle
                                                        initialValue={l.qty}
                                                        name={`${l.id}qty`}
                                                        rules={[{ required: true, message: 'Укажите количество' }]}
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            onChange={(value: number | null) => {
                                                                let copyLines = [...lines];
                                                                let line = copyLines?.find((d) => d.id === l.id);
                                                                if (line) {
                                                                    line.qty = value;
                                                                }

                                                                setLines(copyLines);
                                                            }}
                                                        />
                                                    </Form.Item>
                                                    <Space.Addon>шт</Space.Addon>
                                                </Space.Compact>
                                            </td>
                                            <td className='preloading-item-cell'>
                                                <Space.Compact style={{ margin: '0 5px' }}>
                                                    <Form.Item
                                                        noStyle
                                                        initialValue={l.grossWeight}
                                                        name={`${l.id}grossWeight`}
                                                        rules={[{ required: currentCountry?.code !== 'NL', message: 'Укажите вес брутто' }]}
                                                    >
                                                        <InputNumber
                                                            decimalSeparator=','
                                                            min={0}
                                                            stringMode
                                                            step={1}
                                                            onChange={(value: number | null) => {
                                                                let copyLines = [...lines];
                                                                let line = copyLines?.find((d) => d.id === l.id);
                                                                if (line) {
                                                                    line.grossWeight = value;
                                                                }

                                                                setLines(copyLines);
                                                            }}
                                                        />
                                                    </Form.Item>
                                                    <Space.Addon>кг</Space.Addon>
                                                </Space.Compact>
                                            </td>
                                            <td className='preloading-item-cell'>
                                                <Space.Compact style={{ margin: '0 5px' }}>
                                                    <Form.Item
                                                        noStyle
                                                        initialValue={l.volumeWeight}
                                                        name={`${l.id}volumeWeight`}
                                                        rules={[
                                                            { required: currentCountry?.code !== 'NL', message: 'Укажите объемный вес' },
                                                        ]}
                                                    >
                                                        <InputNumber
                                                            decimalSeparator=','
                                                            min={0}
                                                            stringMode
                                                            step={1}
                                                            onChange={(value: number | null) => {
                                                                let copyLines = [...lines];
                                                                let line = copyLines?.find((d) => d.id === l.id);
                                                                if (line) {
                                                                    line.volumeWeight = value;
                                                                }

                                                                setLines(copyLines);
                                                            }}
                                                        />
                                                    </Form.Item>
                                                    <Space.Addon>кг</Space.Addon>
                                                </Space.Compact>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr className='preloading-item-footer'>
                                    <th scope='row' colSpan={2}></th>
                                    <th style={{ padding: 8 }}>
                                        {header && header.entryId ? (
                                            <>
                                                <span>{header.qty} / </span>
                                                <span
                                                    style={{
                                                        color:
                                                            header.qty == lines.reduce((sum, current) => sum + +(current.qty || 0), 0)
                                                                ? 'var(--main-green)'
                                                                : 'var(--main-red)',
                                                    }}
                                                >
                                                    {lines.reduce((sum, current) => sum + +(current.qty || 0), 0)}
                                                </span>
                                            </>
                                        ) : (
                                            <span>{lines.reduce((sum, current) => sum + +(current.qty || 0), 0)}</span>
                                        )}
                                    </th>
                                    <th>
                                        {header && header.entryId ? (
                                            <>
                                                <span>{header.grossWeight} / </span>
                                                <span
                                                    style={{
                                                        color:
                                                            header.grossWeight ==
                                                            lines.reduce((sum, current) => sum + +(current.grossWeight || 0), 0)
                                                                ? 'var(--main-green)'
                                                                : 'var(--main-red)',
                                                    }}
                                                >
                                                    {lines.reduce((sum, current) => sum + +(current.grossWeight || 0), 0)}
                                                </span>
                                            </>
                                        ) : (
                                            <span>{lines.reduce((sum, current) => sum + +(current.grossWeight || 0), 0)}</span>
                                        )}
                                    </th>
                                    <th>
                                        {header && header.entryId ? (
                                            <>
                                                <span>{header.volumeWeight} / </span>
                                                <span
                                                    style={{
                                                        color:
                                                            header.volumeWeight ==
                                                            lines.reduce((sum, current) => sum + +(current.volumeWeight || 0), 0)
                                                                ? 'var(--main-green)'
                                                                : 'var(--main-red)',
                                                    }}
                                                >
                                                    {lines.reduce((sum, current) => sum + +(current.volumeWeight || 0), 0)}
                                                </span>
                                            </>
                                        ) : (
                                            <span>{lines.reduce((sum, current) => sum + +(current.volumeWeight || 0), 0)}</span>
                                        )}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </Form>
                ) : (
                    <Spin description='Загрузка' indicator={<LoadingOutlined spin />} size='large'>
                        <div style={{ padding: 50 }}></div>
                    </Spin>
                )}
            </Modal>

            <Modal
                destroyOnHidden={true}
                width={500}
                title='Привязать маркировку'
                open={assignMarkingOpen}
                okText='ОК'
                onOk={() => {
                    newMarkingForm.submit();
                }}
                onCancel={() => {
                    newMarkingForm.resetFields();
                    setAssignMarkingOpen(false);
                }}
            >
                <Form
                    colon={false}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    style={{ marginTop: 20 }}
                    form={newMarkingForm}
                    onFinish={onAddNewMarking}
                >
                    <Form.Item hidden name='lineId'>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item label='Маркировка' name='code'>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{ span: 8 }}
                        name='consigneeId'
                        label='ID'
                        rules={[{ required: true, message: 'Укажите ID грузополучателя' }]}
                    >
                        <Select
                            showSearch
                            filterOption={(input, option) => (`${option?.label}` as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label && b?.label && a?.label > b?.label ? 1 : -1)}
                            options={consignees.map((a) => {
                                return {
                                    value: a.id,
                                    label: a.code,
                                };
                            })}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default PreloadingItems;
